/* SpawnAnything — procedural music from the synth instrument buffers (kick, snare, hat, bass, pluck, pad).
   'menu': calm 84 BPM pads with a slow pentatonic pluck melody. 'battle': 128 BPM A-minor groove whose layers
   (pad + hats -> bass + kick -> snare + 16th hats -> lead arpeggio) fade in with the fighting intensity 0..1.
   A small look-ahead scheduler driven from the frame loop; no timers, no allocations per note beyond the nodes. */
import { rng } from './synth.js';

const semi = n => Math.pow(2, n / 12);
// chord roots in semitones relative to A, chord tones for the arpeggio
const BATTLE = { bpm: 128, chords: [[0, [0, 3, 7, 12]], [-4, [-4, 0, 3, 8]], [3, [3, 7, 10, 15]], [-2, [-2, 2, 5, 10]]] }; // Am F C G
const MENU = { bpm: 84, chords: [[3, [3, 7, 10, 14]], [0, [0, 3, 7, 10]], [-4, [-4, 0, 3, 7]], [-2, [-2, 2, 5, 9]]] }; // Cmaj7 Am7 Fmaj7 G6
const PENTA = [0, 3, 5, 7, 10, 12, 15];
const ramp = (x, a, b) => Math.max(0, Math.min(1, (x - a) / (b - a)));

export function createMusic(engine) {
  let mode = 'off', want = 'off', step = 0, next = 0, intensity = 0, fade = 1, duck = 1;
  const r = rng(1234);
  let mel = 2;
  const play = (name, when, gain, rate = 1) => engine.play(name, { bus: 'music', when, gain: gain * fade * duck, rate, variant: 0 });

  function schedule(when) {
    const song = mode === 'battle' ? BATTLE : MENU, bar = Math.floor(step / 16) % 4, s = step % 16;
    const [root, tones] = song.chords[bar], I = intensity;
    if (mode === 'battle') {
      if (s === 0) play('pad', when, 0.32, semi(root)); // pad is rendered at A3
      if (s % 2 === 0) play('hat', when, 0.25 + 0.15 * I);
      const l1 = ramp(I, 0.15, 0.35), l2 = ramp(I, 0.4, 0.6), l3 = ramp(I, 0.65, 0.85);
      if (l1 > 0) {
        if (s % 4 === 0) play('kick', when, 0.75 * l1);
        if (s % 2 === 0) play('bass', when, 0.5 * l1, semi(root + (s % 8 === 6 ? 7 : 0)));
      }
      if (l2 > 0) {
        if (s === 4 || s === 12) play('snare', when, 0.5 * l2);
        if (s % 2 === 1) play('hat', when, 0.16 * l2);
        if (s === 14 && bar === 3) play('snare', when, 0.35 * l2);
      }
      if (l3 > 0 && s % 2 === 0) play('pluck', when, 0.22 * l3, semi(tones[(s / 2) % 4] + (s >= 8 ? 12 : 0)));
    } else {
      if (s === 0) { play('pad', when, 0.42, semi(root)); play('pad', when, 0.22, semi(tones[2])); }
      if (s % 4 === 0 && r() < 0.7) {
        mel = Math.max(0, Math.min(PENTA.length - 1, mel + ((r() * 3) | 0) - 1));
        play('pluck', when, 0.2, semi(PENTA[mel]));
      }
      if (s === 8 && r() < 0.5) play('pluck', when, 0.12, semi(tones[3] + 12));
    }
  }

  return {
    /** 'off' | 'menu' | 'battle' */
    set(m) { want = m; },
    get mode() { return mode; },
    setIntensity(x) { intensity = x; },
    /** 0..1 extra attenuation (pause, stingers) */
    setDuck(x) { duck = x; },
    update(dt) {
      const ctx = engine.ctx; if (!engine.ready) return;
      // crossfade between songs: fade out, switch at silence, fade in
      if (want !== mode) { fade -= dt / 0.8; if (fade <= 0) { fade = 0; mode = want; step = 0; next = 0; } }
      else if (fade < 1) fade = Math.min(1, fade + dt / 1.2);
      if (mode === 'off') return;
      const now = ctx.currentTime, sp = 60 / (mode === 'battle' ? BATTLE.bpm : MENU.bpm) / 4;
      if (next < now) next = now + 0.05; // after a hidden tab / first start
      while (next < now + 0.25) { schedule(next); step++; next += sp; }
    },
  };
}
